import { Dispatch, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getTemplatePreviewThumbnails } from 'api/TemplateIndexAPI';

import { WorkflowNodeCampaign } from '../types';
import { selectThumbnails } from '../selectors';
import { handleThumbnailFetchFailure, handleThumbnailFetchSuccess } from '../actions';
import { THUMBNAIL_REQUEST_PAGE_SIZE } from '../constants';

/** Splits an array into pages (i.e. [1, 2, 3, 4, 5] => [[1, 2], [3, 4], [5]]) */
const paginateArray = <T>(array: T[], pageSize: number): T[][] =>
  array.reduce(
    (pages, item) => {
      const lastPage = pages[pages.length - 1];
      if (lastPage.length < pageSize) {
        lastPage.push(item);
      } else {
        pages.push([item]);
      }
      return pages;
    },
    [[]] as T[][],
  );

/** Fetches thumbnail URLs from API, then dispatches thumbnails to Redux for caching */
const fetchAndCacheThumbnails = async (dispatch: Dispatch<any>, templateIds: number[]) => {
  try {
    const thumbnails = await getTemplatePreviewThumbnails(templateIds);
    dispatch(handleThumbnailFetchSuccess(templateIds, thumbnails));
  } catch (e) {
    dispatch(handleThumbnailFetchFailure(templateIds));
  }
};

/** Determines if a thumbnail has already been loaded in the Redux cache given a template ID */
const shouldFetchThumbnailForTemplateId = (
  templateId: number | undefined,
  cachedThumbnails: Record<number, string | null>,
) => !!templateId && cachedThumbnails[templateId] === undefined;

/** Ensures that all thumbnails are loaded for `campaigns` into Redux. */
export const useCampaignThumbnails = (campaigns: WorkflowNodeCampaign[]) => {
  const dispatch = useDispatch();
  const cachedThumbnails = useSelector(selectThumbnails);
  const shouldLoadThumbails = campaigns.some((campaign) =>
    shouldFetchThumbnailForTemplateId(campaign.templateId, cachedThumbnails),
  );

  useEffect(() => {
    if (shouldLoadThumbails) {
      const templateIdsToLoad = campaigns
        .map(({ templateId }) => templateId)
        .filter((templateId): templateId is number =>
          shouldFetchThumbnailForTemplateId(templateId, cachedThumbnails),
        );

      paginateArray(templateIdsToLoad, THUMBNAIL_REQUEST_PAGE_SIZE).forEach(
        (pageOfTemplateIdsToFetch) => fetchAndCacheThumbnails(dispatch, pageOfTemplateIdsToFetch),
      );
    }
  }, [shouldLoadThumbails]);
};
